/* eslint no-bitwise: ["error", { "int32Hint": true }] */
import React from 'react';
import axios from 'axios';
import { Map } from 'immutable';
import { get, isEmpty } from 'lodash/fp';
import isArrayBuffer from 'lodash/isArrayBuffer';
import { put, call, fork, select, cancelled, all, delay, race, take, actionChannel } from 'redux-saga/effects';
import { signOutAction, addDataLoadingAction, removeDataLoadingAction } from 'domain/env';
import find from 'lodash/find';
import { notify } from 'lib/errorLogger';
import Api, { doLogout, logoutAction } from 'domain/api';
import { getRuntimeConfig } from 'lib/runtimeConfig';

import { isGridWorkSpaceSelector } from 'domain/companies/companiesSelector';
import { queryToSearch } from 'domain/router/helpers';
import { parseSearchAggregations } from 'domain/documents/searchAggregations';
import { queryClient } from 'services/query';
import { documentsKeys } from 'domain/documents/queries/keys';
import {
  documentsAdapter,
  documentAdapter,
  documentAdapterValidated,
  documentsListAdapter,
  updateGridConfigChanges,
  getCurrentStoredConfig,
  deleteGridConfigChanges,
  decodeArrayBufferText,
  mergeDocumentsMapWithPreviousGridMeta,
} from 'domain/documents/helpers';
import { navigate } from 'domain/router/redux/reduxActions';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import { generatePath } from 'react-router-dom';
import * as Journal from 'domain/journal/sagas';
import * as Router from 'domain/router';
import { messagesAdapter } from 'domain/journal/adapters';
import * as AgGridActions from 'domain/agGrid';
import { jeInvoiceTypeSelector } from 'domain/journal/selectors';
import download from 'lib/download';
import {
  yieldSuccessNotification,
  yieldErrorNotification,
  yieldInfoNotification,
  yieldWarningNotification,
  handleErrorResponseWithToast,
} from 'lib/toasts';
import { FormattedMessage } from 'react-intl';
import * as actions from 'domain/documents/documentsActions';
import * as selector from 'domain/documents/documentSelector';
import { userGUIDSelector, userIdSelector } from 'domain/env/envSelector';
import { currentConnectedERPselector } from 'domain/settings';
import { routing } from 'domain/router';
import { matchRoute } from 'domain/router/utils';
import { getNestedCategory } from 'domain/categories/helpers';

import { executeFunc } from 'lib/helpers';
import { ToastEventHandler } from 'components/Tables/toast';
import * as ACL from 'domain/restriction';
import * as adapters from 'domain/documents/adapters';
import { decideNavigation } from 'domain/approvals/navigation/decideNavigation';
import { getNavigationDecisionInput } from 'domain/approvals/navigation/getNavigationDecisionInput';
import { applyPostActionNavigation } from 'domain/approvals/navigation/applyPostActionNavigation';
import { yieldToastForPostAction } from 'domain/approvals/navigation/yieldToastForPostAction';
import { documentPublishCompletedAction } from 'domain/ws/actions';
import { documentsApi } from 'domain/documents/api';
import { ApiSchemaError } from 'services/api';

const WS_EVENT_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
const PUBLISH_POLL_INTERVAL_MS = 3 * 1000; // DA-19604: backup poll cadence when WS is blocked

import indexedDb from 'indexedDb';
import CONST from 'domain/documents/constants';
import tokenKeeper from 'lib/apiTokenKeeper';
import { PublishTypes } from 'pages/document/components/DocumentPublishButton/DropdownButton/config';
import { ensureGetExtractedTableData } from 'domain/textract/sagas';
import { storage } from 'lib/storage';

import {
  ensureWorkspaceGridApplyTransaction,
  ensureSearchDocuments,
  ensureWorkspaceGridRefreshServerSide,
} from 'domain/documents/sagas/sagas.workspace';

import Link from '@mui/material/Link';

export { selector, actions };

export * from 'domain/documents/sagas/sagas.navigation';
export * from 'domain/documents/sagas/sagas.workspace';
export * from 'domain/documents/sagas/sagas.moveTo';

export function* ensureDocumentsExportFormats() {
  yield put({ type: actions.documentGetExportFormats.request });
  try {
    const { data } = yield call(Api.getDocumentsExportFormats, {
      params: {},
    });
    yield put({
      type: actions.documentGetExportFormats.success,
      payload: new Map(data),
    });
  } catch (err) {
    yield doLogout(actions.documentGetExportFormats, err);
  } finally {
    if (yield cancelled()) {
      yield put({
        type: actions.documentGetExportFormats.failure,
        err: 'cancelled',
      });
    }
  }
}

export function* duplicateDocument({ payload: { id, resolve, reject } }) {
  try {
    yield call(Api.duplicateDocument, {
      data: {
        documentID: id,
      },
    });
    yield put({
      type: actions.duplicateDocumentAction.success,
      // payload: adapters.documentAdapter(data),
    });
    yieldSuccessNotification(
      <FormattedMessage id="document.actions.duplicate" defaultMessage="Document successfully duplicated" />,
    );
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject(err);
    yield doLogout(actions.duplicateDocumentAction, err);
  }
}

export function* ensureGetDocument(documentID, companyId, isLoader = true) {
  if (isLoader) {
    yield put({
      type: actions.documentGet.request,
    });
  }

  try {
    const { data } = yield call(Api.getDocument, {
      params: { documentID },
    });
    yield put({
      type: actions.documentGet.success,
      payload: documentAdapterValidated(data),
    });
    return data;
  } catch (err) {
    if (
      err &&
      err.response &&
      err.response.status &&
      err.response.status.toString() === process.env.REACT_APP_NOT_FOUND_CODE
    ) {
      yield put(navigate.push(generatePath(ROUTES_PATH.DOCUMENT_NOT_FOUND.absolute, { companyId })));
    }
    yield doLogout(actions.documentGet, err);
  }
}

export function* ensureGetDocumentLinkedCount({ linkedID, resolve, reject }) {
  try {
    const { data } = yield call(Api.getDocumentLinkedCount, {
      params: {
        linkedID,
      },
    });
    if (typeof resolve === 'function') resolve(data);
  } catch (err) {
    yield doLogout(actions.documentGetLinkedCountAction, err);
    if (typeof resolve === 'function') reject();
  }
}

export function* ensureGetUnreadRequestsAction() {
  yield put({
    type: actions.getUnreadRequestsAction.request,
  });

  try {
    const { data } = yield call(Api.getUnreadRequests, {
      params: {},
    });
    yield put({
      type: actions.getUnreadRequestsAction.success,
      payload: adapters.unredRequestDocsAdapter(data),
    });
  } catch (err) {
    yield put({
      type: actions.getUnreadRequestsAction.failure,
    });
  }
}

export function* ensureUpdateDocumentTags(payload) {
  const { documentID, tagsToAdd, tagsToRemove, resolve, reject } = payload;
  try {
    const { data } = yield call(Api.updateDocumentTags, {
      data: {
        documentID,
        tagsToAdd: tagsToAdd.join(','),
        tagsToRemove: tagsToRemove.join(','),
      },
    });
    yield put({
      type: actions.documentUpdate.success,
      payload: documentAdapter(data),
    });
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield doLogout(actions.documentUpdateTags, err);
  }
}

export function* ensureUpdateMultipleDocumentsTags({ payload }) {
  const { documentID, tagsToAdd, tagsToRemove, resolve, reject } = payload;
  try {
    const { data } = yield call(Api.updateMultipleDocumentsTags, {
      data: {
        documentID,
        tagsToAdd: tagsToAdd.join(','),
        tagsToRemove: tagsToRemove.join(','),
      },
    });
    yield put({
      type: actions.documentMultipleUpdateTags.success,
      payload: documentsListAdapter(data.documents),
    });
    if (data?.success?.length > 0) {
      // !!!!IMPORTANT
      // for 'update' transaction type - invoke ensureWorkspaceGridApplyTransaction after dispatch main action to redux
      // - we get actual/merged documents in ensureWorkspaceGridApplyTransaction for transaction
      yield fork(ensureWorkspaceGridApplyTransaction, {
        transactionType: 'update',
        nodes: data.success,
      });
    }
    if (typeof resolve === 'function') {
      resolve({ success: data.success.length, total: documentID.size });
    }
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield doLogout(actions.documentMultipleUpdateTags, err);
  }
}

export function* ensureUpdateDocumentTagsInList(payload) {
  const { documentID, tagsToAdd, tagsToRemove, resolve, reject } = payload;
  try {
    const { data } = yield call(Api.updateDocumentTags, {
      data: {
        documentID,
        tagsToAdd: tagsToAdd.join(','),
        tagsToRemove: tagsToRemove.join(','),
      },
    });
    yield put({
      type: actions.documentUpdateTagsInList.success,
      payload: documentAdapter(data),
    });
    // !!!!IMPORTANT
    // for 'update' transaction type - invoke ensureWorkspaceGridApplyTransaction after dispatch main action to redux
    // - we get actual/merged documents in ensureWorkspaceGridApplyTransaction for transaction
    yield fork(ensureWorkspaceGridApplyTransaction, {
      transactionType: 'update',
      nodes: [data.documentID],
    });
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield doLogout(actions.documentUpdateTags, err);
  }
}

export function* ensureLinkDocumentInList(payload) {
  const { documentID, linkID, text, params, resolve = (x) => x, reject = (x) => x } = payload;
  const documentIds = Array.isArray(documentID) ? documentID : [documentID];
  const existingLinkID = yield select(selector.documentLinkedTagSelector);

  const linkTag = linkID || existingLinkID;
  try {
    const { data } = yield call(Api.linkDocuments, {
      data: {
        documentID: documentIds,
        linkID: linkTag,
        text,
      },
    });

    const list = yield select(selector.documentsByIdSelector);
    // request for all the documents that are currently in a list after unlinkin
    yield call(ensureSearchDocuments, { params, force: true }, list.size);

    const { tag } = yield select(selector.linkedSelector);

    if (tag === linkTag) {
      yield put({
        type: actions.documentGetLinkedAction.type,
        payload: {
          tag: linkTag,
        },
      });
    }

    resolve(data);
  } catch (err) {
    reject(err);
    yield doLogout(actions.documentLinkInListAction, err);
  }
}

export function* ensureLockLinkedDocs(payload) {
  const { linkID, locked, resolve, reject } = payload;

  try {
    const { data } = yield call(Api.lockLinkedDocuments, {
      data: { linkID, locked },
    });

    const newLinkId = data.items[0].linkid;

    yield put({
      type: actions.lockLinkedDocsAction.success,
      payload: {
        list: documentsAdapter({ ...data }).list,
        tag: newLinkId,
      },
    });

    yield fork(ensureWorkspaceGridApplyTransaction, {
      transactionType: 'update',
      nodes: data?.items.map((item) => item.documentID),
    });

    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield doLogout(actions.lockLinkedDocsAction, err);
  }
}

export function* ensureUnlinkDocumentInList(payload) {
  const { documentID, params, resolve = (x) => x, reject = (x) => x } = payload;
  const documentIds = Array.isArray(documentID) ? documentID : [documentID];

  try {
    const { data } = yield call(Api.unlinkMultiDocuments, {
      data: {
        documentID: documentIds,
      },
    });

    const isGrid = yield select(isGridWorkSpaceSelector);

    if (isGrid) {
      yield put({
        type: AgGridActions.agGridRefreshServerSideAction.type,
        payload: false,
      });
    } else {
      const list = yield select(selector.documentsByIdSelector);
      // request for all the documents that are currently in a list after unlinkin
      yield call(ensureSearchDocuments, { params, force: true }, list.size);
    }

    resolve(data);
  } catch (err) {
    reject();
    yield doLogout(actions.documentLinkInListAction, err);
  }
}

export function* ensureUpdateLink(payload) {
  const { linkID, text, resolve, reject } = payload;
  try {
    const { data } = yield call(Api.updateLink, {
      data: {
        linkID,
        text,
      },
    });

    // @to-do process event
    yield put({
      type: actions.documentUpdateLinkAction.success,
      payload: documentsAdapter({ ...data }),
    });
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield doLogout(actions.documentLinkInListAction, err);
  }
}

// DA-19604: one poll of the publish background-task result (DA-19619). The
// `endpoint()` client validates and returns the parsed body; a 404 (no result
// yet / expired) or a transient error is swallowed so the fallback keeps polling.
function* safeGetAcceptResult(documentID) {
  try {
    return yield call(documentsApi.getAcceptResult, { path: { documentID } });
  } catch (err) {
    // Response-schema drift is a real contract bug — rethrow so it surfaces once
    // (endpoint() throws ApiSchemaError in non-prod) instead of re-firing a
    // mismatch toast every poll tick. A 404 (no result yet / expired) or a
    // transient transport error is expected while polling → swallow and retry.
    if (err instanceof ApiSchemaError) throw err;
    return null;
  }
}

// DA-19604: backup completion signal for clients whose WebSocket is blocked.
// Polls the accept-result endpoint every PUBLISH_POLL_INTERVAL_MS and resolves
// only on a terminal outcome (`success`/`failed`). `pending` and a not-yet
// -available result keep it waiting. Bounded by the outer race timeout in
// ensureAcceptDocument, which cancels this fiber once any branch wins — so
// recursion depth is capped and no poll runs after the wait resolves.
export function* pollAcceptResult(documentID) {
  yield delay(PUBLISH_POLL_INTERVAL_MS);
  const result = yield* safeGetAcceptResult(documentID);
  const isTerminal = result?.status === 'success' || result?.status === 'failed';
  return isTerminal ? result : yield* pollAcceptResult(documentID);
}

// DA-19604/DA-19052: show an async-operation completion (publish or ERP
// force-sync) to the current user — structured messages[] via the JE toast
// renderer plus an optional top-level warning. Guards on user_id because WS
// completions are broadcast to all viewers; only the initiating user sees them.
export function* yieldAsyncOperationToast({ payload }) {
  const { messages, warning, user_id } = payload || {};
  const safeMessages = Array.isArray(messages) ? messages : [];
  const userGUID = yield select(userGUIDSelector);
  if (String(user_id) !== String(userGUID)) return;
  if (safeMessages.length > 0) {
    ToastEventHandler.onResponce(safeMessages);
  }
  if (warning) {
    yieldWarningNotification(warning);
  }
}

export function* ensureAcceptDocument(
  documentID,
  { payload: { resolve, reject, force, remainOnDocument, userConfirmed, publishType } },
) {
  yield put({ type: actions.documentAccept.request });

  // DA-19603: The publish HTTP request and the WS DOCUMENT_PUBLISH_COMPLETED
  // message race each other. Under throttling the request can resolve AFTER the
  // WS message has already been dispatched, so a plain `take(...)` (which only
  // sees actions dispatched after it starts) misses the event and falls through
  // to the timeout branch. An actionChannel created BEFORE the request buffers
  // the completion event, so `take(publishChannel)` resolves immediately if it
  // already arrived, or waits if it hasn't yet. Filter by document_id so an
  // overlapping publish of a different doc can't satisfy this wait.
  const publishChannel = yield actionChannel(
    (action) =>
      action.type === documentPublishCompletedAction.type && action.payload?.document_id === documentID,
  );

  try {
    yield call(Api.acceptDocumentAsBackgroundTask, {
      documentID,
      params: {
        force: force | 0, // bitwise type casting
      },
    });

    yield put({
      type: actions.documentsJustPublishedAction.type,
      payload: publishType,
    });

    // Wait for publish completion before removing loader. The WS event (drained
    // from the buffered publishChannel) is the primary signal; DA-19604 adds a
    // backend poll as a backup for clients whose WS is blocked. Whichever reports
    // a terminal outcome first wins; the race cancels the loser, so polling stops
    // once the WS event resolves the wait — and since the first poll only fires
    // after PUBLISH_POLL_INTERVAL_MS, a client whose WS arrives first typically
    // issues no poll request at all. Bounded by WS_EVENT_TIMEOUT_MS.
    const { timeout, wsEvent, polled } = yield race({
      wsEvent: take(publishChannel),
      polled: call(pollAcceptResult, documentID),
      timeout: delay(WS_EVENT_TIMEOUT_MS),
    });

    // The WS payload and the poll result share a shape by backend design (DA-19619).
    // The poll result carries no user_id (it only ever tracks the current user's
    // own publish), so stamp the current user's GUID for the guard in the shared
    // toast helper; the WS payload already carries the originating user_id.
    const userGUID = yield select(userGUIDSelector);
    const publishResult = wsEvent?.payload ?? (polled ? { ...polled, user_id: userGUID } : null);

    // DA-19604: show completion feedback HERE — the single source now that
    // `documentPublishCompletedAction` was removed from the global WS takeEvery
    // (pages/document/sagas). A late DUPLICATE WS event (a slow-but-not-blocked
    // socket arriving after the poll already won) no longer has a handler, so it
    // cannot re-fire the toast (Copilot review fix).
    if (publishResult) {
      yield* yieldAsyncOperationToast({ payload: publishResult });
    }

    if (timeout) {
      ToastEventHandler.onResponceTranslate({
        id: 'document.journalEntry.toast.publish_completion_timeout',
        defaultMessage:
          'Document publishing is taking longer than expected. Please refresh the page to check the current status',
        type: 'error',
      });
    }

    yield put({ type: removeDataLoadingAction.type });

    // Re-fetch document and JE to get post-publish state (isLoader=false to avoid extra loader)
    const companyId = yield select(selector.currentCompanyIdSelector);
    const docData = yield call(ensureGetDocument, documentID, companyId, false);
    // DA-19119: ensureGetDocument dispatches documentGet.success which updates
    // state.document but NOT state.list. Dispatch documentUpdateInListByID so the
    // nav list (documentsByIdSelector) also gets the fresh tags (_S_ACCEPTED)
    // without overwriting the validated state.document.
    if (docData) {
      yield put({ type: actions.documentUpdateInListByID.type, payload: documentAdapter(docData) });
    }
    yield call(Journal.ensureJounalEntry, documentID);

    // Navigate based on fresh post-publish state
    const decisionInput = yield* getNavigationDecisionInput();
    const postAction = decideNavigation({
      action: remainOnDocument ? 'publishAndStay' : 'publish',
      ...decisionInput,
    });

    // Reject if the publish outcome (from either the WS event or the DA-19604
    // poll) reported failure — clear justPublished so a subsequent approve on the
    // same doc doesn't think it was published (DA-19127: stale marker → incorrect
    // "Well Done" redirect).
    if (publishResult?.status === 'failed') {
      yield put({
        type: actions.documentsJustPublishedAction.type,
        payload: null,
      });
      if (typeof reject === 'function') reject();
      return;
    }

    if (typeof resolve === 'function') resolve();

    yield* applyPostActionNavigation(postAction);
  } catch (err) {
    if (typeof reject === 'function') reject();

    // 409 Conflict — publish already in progress
    if (err.response && err.response.status === 409) {
      yieldWarningNotification(err.response.data?.message);
      yield put({ type: removeDataLoadingAction.type });
      return;
    }

    // this checks for network error (lost connection for instance)
    // in more or less crossbrowser manner
    if (typeof err.response === 'undefined') {
      ToastEventHandler.onResponceTranslate({
        id: 'document.journalEntry.toast.publish_network_error',
        defaultMessage:
          'We experienced network error while document was being published. Please refresh the page and try again if document remains unpublished',
        type: 'error',
      });
    }
    yield doLogout(actions.documentAccept, err);
  } finally {
    // Unsubscribe the buffered channel so it stops queuing completion events
    // for this document after the saga is done (also runs on cancellation).
    publishChannel.close();
  }
}

export function* ensureDocumentScheduleAccept(documentsIds, { payload: { resolve, reject, userConfirmed } }) {
  // Decide navigation up-front: Quick publish only schedules the publish on
  // the backend, so it does not synchronously change any input that
  // `decideNavigation` reads (queue, accepted status). Computing `postAction`
  // before the API lets us gate the JE refresh on the same "stay" signal
  // we'll act on after — fixes DA-18892 (BK with own pending approval on a
  // single financial doc Quick-publishes → no next doc → STAY → JE was
  // stale because the legacy `remainOnDocument` gate, set from the
  // now-removed `isBKCurrentApprovalForFinancialDoc`, no longer fires).
  const decisionInput = yield* getNavigationDecisionInput();
  const postAction = decideNavigation({ action: 'quickPublish', ...decisionInput });
  const isNeedUpdateJE = postAction === 'stay' && documentsIds.length === 1;

  try {
    if (isNeedUpdateJE) {
      yield put({ type: addDataLoadingAction.type });
    }
    yield call(Api.bulkAcceptDocuments, {
      data: {
        documentID: documentsIds,
        userConfirmed,
      },
    });
    if (isNeedUpdateJE) {
      yield call(Journal.ensureJounalEntry, documentsIds[0]);
      yield put({
        type: actions.documentsJustPublishedAction.type,
        payload: PublishTypes.SchedulePublish,
      });
      yield put({ type: removeDataLoadingAction.type });
    }

    yield* yieldToastForPostAction(postAction, 'info', {
      id: 'document.actions.schedulePublish.success',
      defaultMessage: 'Document scheduled for publishing',
    });

    if (typeof resolve === 'function') resolve();

    yield* applyPostActionNavigation(postAction);
  } catch (err) {
    if (isNeedUpdateJE) {
      yield put({ type: removeDataLoadingAction.type });
    }
    if (typeof reject === 'function') reject();
    yield doLogout(actions.documentScheduleAcceptAction, err);
  }
}

export function* ensureRejectDocument(documentID, reason, { resolve, reject }) {
  try {
    const { data } = yield call(Api.rejectDocument, {
      data: {
        documentID,
        reason,
      },
    });
    yield put({
      type: actions.documentUpdate.success,
      payload: documentAdapter(data),
    });
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield doLogout(actions.documentReject, err);
  }
}

export function* ensureRemoveDocument({ documentID, resolve, reject }) {
  yield put({
    type: actions.documentRemove.request,
    payload: documentID,
  });
  try {
    const { data } = yield call(Api.removeDocument, {
      data: { documentID },
    });

    yield put({
      type: actions.documentRemove.success,
      payload: {
        id: documentID,
        data,
      },
    });

    yield fork(ensureWorkspaceGridApplyTransaction, {
      transactionType: 'remove',
      nodes: [documentID],
    });
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    const error = err?.response?.data;
    if (error) yieldErrorNotification(error);
    yield doLogout(actions.documentRemove, err);
    if (typeof reject === 'function') reject(err);
  }
}

export function* ensureRemoveDocumentNotes({ documentID, resolve, reject }) {
  try {
    const { data } = yield call(Api.removeNotes, {
      data: { documentID },
    });
    yield put({
      type: actions.documentUpdate.success,
      payload: documentAdapter(data),
    });
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    const error = err?.response?.data?.message;
    if (error) {
      yieldErrorNotification(error);
    } else {
      yieldErrorNotification(<FormattedMessage id="app.generalUnknownError" defaultMessage="Something went wrong" />);
    }
    if (typeof reject === 'function') reject(err);
    yield doLogout(actions.documentUpdate, err);
  }
}

export function* ensureRemoveDocumentNotesInList({ documentID, resolve, reject }) {
  try {
    const { data } = yield call(Api.removeNotes, {
      data: { documentID },
    });
    yield put({
      type: actions.documentRemoveNotesInList.success,
      payload: documentAdapter(data),
    });
    resolve();
  } catch (err) {
    const error = err?.response?.data?.message;
    if (error) {
      yieldErrorNotification(error);
    } else {
      yieldErrorNotification(<FormattedMessage id="app.generalUnknownError" defaultMessage="Something went wrong" />);
    }
    if (typeof reject === 'function') reject(err);
    yield doLogout(actions.documentRemoveNotesInList, err);
  }
}

export function* ensureReadDocument(routeDocumentID, componentDocumentID) {
  // Event might be dispatched on pdf load event when
  // user has already left this document page and route already has another document id.
  // In this case we are not performing update of componentDocumentID as user
  // left its page before render completed.
  // The issue was that event was dispatched for componentDocumentID and we updated document
  // with routeDocumentID
  if (routeDocumentID === componentDocumentID) {
    try {
      const { data } = yield call(Api.updateDocumentTags, {
        data: {
          documentID: routeDocumentID,
          tagsToAdd: '_S_READ',
          tagsToRemove: '_S_NEW',
        },
      });
      yield put({
        type: actions.documentUpdate.success,
        payload: documentAdapter(data),
      });
    } catch (err) {
      yield doLogout(actions.documentUpdateTags, err);
    }
  }
}

export function* ensureUpdateDocumentNotesShow({ documentID, notes, notesColor, resolve, reject }) {
  try {
    const { data } = yield call(Api.updateNotes, {
      data: {
        documentID,
        notes,
        notes_color: notesColor,
      },
    });
    yield put({
      type: actions.documentUpdate.success,
      payload: documentAdapter(data),
    });
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    const error = err?.response?.data?.message;
    if (error) {
      yieldErrorNotification(error);
    } else {
      yieldErrorNotification(<FormattedMessage id="app.generalUnknownError" defaultMessage="Something went wrong" />);
    }
    yield doLogout(actions.documentUpdateTags, err);
    if (typeof reject === 'function') reject(err);
  }
}

export function* ensureSetDocumentAsCover(payload) {
  const { documentId: documentID, resolve, reject } = payload;
  try {
    const { data } = yield call(Api.setDocumentAsCover, {
      data: {
        documentID,
      },
    });
    yield put({
      type: actions.documentSetAsCoverAction.success,
      payload: { ...documentAdapter(data), documentID },
    });

    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject(err);
    yield doLogout(actions.documentSetAsCoverAction, err);
  }
}

export function* ensureGetMRUTags() {
  const companyId = yield select(selector.currentCompanyIdSelector);

  try {
    const { data } = yield call(Api.getMRUTags, {
      params: {},
    });
    yield put({
      type: actions.documentGetMRUTags.success,
      payload: data,
      companyId,
    });
  } catch (err) {
    yield doLogout(actions.documentGetMRUTags, err);
  }
}

export function* ensureWorkerSearchDocuments(matchParams) {
  function* prepare(params) {
    const { category } = params;

    const list = yield select(selector.documentsByIdSelector);
    const query = yield select(Router.querySelector);
    const subtype = yield select(selector.subtypeFilterSelector);
    const { companyType, companyId } = params;
    const workspace = companyType ? { companyType } : {};
    return {
      maxResults: list.count() < 30 ? 30 : list.count(),
      category,
      companyId,
      ...query,
      ...(subtype ? { subtags: [subtype] } : {}),
      isConfidential: workspace && workspace.companyType === 'confidential',
    };
  }

  return {
    // apiName: 'documentSearch',
    apiName: 'documentSearchByCategory',
    config: {
      params: yield call(prepare, matchParams),
    },
    prepare,
    success(data) {
      // DA-19430: tabs/subtypes ride the search_new response top-level (no `aggregations`
      // wrapper). undefined when the category is not tab-eligible (backend sends tabs:null).
      const aggregations = parseSearchAggregations(data);
      // CRIT (DA-19430): keep an empty document-type tab selected. A Fin/Gen tab is a routed
      // category with its own (possibly zero) count — only bounce empty REAL categories.
      const isEmptyDoctypeTab = (aggregations?.tabs ?? []).some(
        (t) => t.name_id !== 'all' && t.id === matchParams.category,
      );
      if (!data?.count && matchParams.category !== matchParams.category1 && !isEmptyDoctypeTab) {
        const route =
          matchParams?.companyType === 'confidential'
            ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute
            : ROUTES_PATH.COMPANY_WORKSPACE.absolute;
        return navigate.push(generatePath(route, { ...matchParams }));
      }
      return {
        type: actions.documentWorkerSearch.success,
        payload: documentsAdapter(data),
        aggregations,
      };
    },
    failure(err) {
      return logoutAction(actions.documentSearchAction, err);
    },
  };
}

export function* ensureWorkerSearchGridDocuments(matchParams) {
  // eslint-disable-next-line require-yield
  function* prepare() {
    return {};
  }

  return {
    apiName: 'documentLatestCompanyTimestamp',
    config: {
      params: yield call(prepare, matchParams),
    },
    prepare,
    success(data) {
      const { timeStamp } = data;

      return {
        type: actions.documentWorkerCompanyLatestTimestamp.success,
        payload: timeStamp,
      };
    },
    failure(err) {
      return logoutAction(actions.documentWorkerCompanyLatestTimestamp, err);
    },
  };
}

export function* ensureWorkerTransactionErp(documentID) {
  // eslint-disable-next-line require-yield
  function* prepare() {
    return {
      documentID,
    };
  }
  function* checkDisabled() {
    const s = yield select(selector.erpDuplicatedSelector);
    const isGranted = yield select(ACL.isGranted);
    if (!isGranted(ACL.CAN_CHECK_DUPLICATE_ERP_DOCS)) return true;
    return s.has(documentID);
  }
  return {
    apiName: 'getTransactionErp',
    prepare,
    interval: getRuntimeConfig().env.REACT_APP_ERP_TRANSACTION_POLLING_INTERVAL || 30000,
    config: {
      params: yield call(prepare),
    },
    disabled: yield call(checkDisabled),
    success(data) {
      if (data !== null) {
        // this API return just toast about duplication
        const message = messagesAdapter(data.messages).get(0);
        ToastEventHandler.onResponseForDuplicate(message);
        return {
          type: actions.documentWorkerTransactionErp.success,
          payload: { documentID },
        };
      }
      return {
        type: actions.documentWorkerTransactionErp.request,
      };
    },
    failure(err) {
      return logoutAction(actions.documentWorkerTransactionErp, err);
    },
  };
}

export function* ensureUnlinkDocuments(payload) {
  const { linkid, params, resolve } = payload;
  try {
    yield call(Api.unlinkDocuments, {
      data: {
        linkID: linkid,
      },
    });

    yieldSuccessNotification(
      <FormattedMessage id="toast.unlinkDocuments" defaultMessage="Document unlinked successfully" />,
    );

    const list = yield select(selector.documentsByIdSelector);
    // request for all the documents that are currently in a list after unlinkin
    yield call(ensureSearchDocuments, { params, force: true }, list.size);
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    yield doLogout(actions.documentsUnlink, err);
  }
}

export function* ensureGetLinkedDocuments({ tag, pageToken, resolve, reject, isOpen = true }) {
  const linked = yield select(selector.linkedSelector);
  yield put({ type: actions.documentGetLinkedAction.request });
  try {
    const { data } = yield call(Api.documentSearch, {
      params: {
        maxResults: 20,
        searchQuery: `(linkid: ${tag})`,
        pageToken,
      },
      paramsSerializer: (d) => queryToSearch(d),
    });

    yield put({
      type: actions.documentGetLinkedAction.success,
      payload: {
        list:
          linked.tag === tag && pageToken
            ? linked.list.merge(documentsListAdapter(data.items))
            : documentsListAdapter(data.items),
        tag,
        isOpen,
        count: data.count,
        pageToken: data.pageToken,
      },
    });

    if (typeof resolve === 'function') resolve(data);
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield put({ type: actions.documentGetLinkedAction.failure, err });
  }
}

export function* ensureGetLinkedDocumentsForPreview({
  tag,
  pageToken,
  resolve,
  reject,
  isOpen = true,
  searchedDocumentId,
}) {
  const linked = yield select(selector.linkedSelector);
  yield put({ type: actions.documentGetLinkedForPreviewAction.request });
  try {
    let currentPageToken = pageToken || '';
    let count = '';
    let isNotFoundDocument = false;
    const items = [];
    do {
      const { data } = yield call(Api.documentSearch, {
        params: {
          maxResults: 20,
          searchQuery: `(linkid: ${tag})`,
          pageToken: currentPageToken,
        },
        paramsSerializer: (d) => queryToSearch(d),
      });
      currentPageToken = data.pageToken;
      items.push(...data.items);
      count = data.count;
      isNotFoundDocument = data.items.findIndex(({ documentID }) => documentID === searchedDocumentId) === -1;
    } while (currentPageToken !== null && searchedDocumentId !== null && isNotFoundDocument);

    yield put({
      type: actions.documentGetLinkedForPreviewAction.success,
      payload: {
        list:
          linked.tag === tag && currentPageToken
            ? linked.list.merge(documentsListAdapter(items))
            : documentsListAdapter(items),
        tag,
        isOpen,
        count,
        pageToken: currentPageToken,
      },
    });

    if (typeof resolve === 'function') resolve(items);
  } catch (err) {
    if (typeof reject === 'function') reject();
    yield put({ type: actions.documentGetLinkedForPreviewAction.failure, err });
  }
}

export function* ensureGetLinkedDocumentsIDs({ linkid, resolve, reject }) {
  try {
    let pageToken = '';
    const res = [];
    do {
      const { data } = yield call(Api.documentSearch, {
        params: {
          maxResults: 1000,
          searchQuery: `(linkid: ${linkid})`,
          pageToken,
        },
        paramsSerializer: (d) => queryToSearch(d),
      });
      pageToken = data.pageToken;
      res.push(...data.items.filter(({ protected: isProtected }) => !isProtected).map(({ documentID }) => documentID));
    } while (pageToken != null);
    if (typeof resolve === 'function') resolve(res);
    return res;
  } catch (err) {
    if (typeof reject === 'function') reject();
  }
}

export function* ensureMarkAsPaid({ payload: { documentId, isPaid, needJESync = true } }) {
  const requestData = {
    documentID: documentId,
    document_type: isPaid ? 'paid' : 'unpaid',
  };
  const isGranted = yield select(ACL.isGranted);

  try {
    const { data } = yield call(Api.changeTypeDocument, { data: requestData });
    const adaptedDocument = documentAdapterValidated(data);

    yield put({
      type: actions.markAsPaidAction.success,
      payload: adaptedDocument,
    });

    // !!!!IMPORTANT
    // for 'update' transaction type - invoke ensureWorkspaceGridApplyTransaction after dispatch main action to redux
    // - we get actual/merged documents in ensureWorkspaceGridApplyTransaction for transaction
    yield fork(ensureWorkspaceGridApplyTransaction, {
      transactionType: 'update',
      nodes: [data.documentID],
    });

    // DA-19201: replaced ensureSyncJE (which awaited a WS event that never arrives on workspace grid)
    // with a simple JE fetch to update document JE in Redux
    if (needJESync && isGranted(ACL.IS_WORK_ON_WITH_CURRENT_DOCUMENT_AS_FINANCIAL)) {
      yield call(Journal.ensureJounalEntry, documentId);
    }

    const [id, msg] = isPaid ? ['paid', 'Paid'] : ['unpaid', 'Unpaid'];
    yieldSuccessNotification(
      <FormattedMessage id={`documents.${id}.toast`} defaultMessage={`Document marked as ${msg}`} />,
    );
  } catch (err) {
    const error = err?.response?.data?.message;

    if (error) {
      yieldErrorNotification(error);
    } else {
      yieldErrorNotification(<FormattedMessage id="app.generalUnknownError" defaultMessage="Something went wrong" />);
    }

    yield doLogout(actions.markAsPaidAction, err);
  }
}

export function* ensureUpdateDocumentNotes({ payload: { notes, notesColor, documentID, cb } }) {
  try {
    const { data } = yield call(Api.updateNotes, {
      data: {
        notes,
        documentID,
        notes_color: notesColor,
      },
    });
    yield put({
      type: actions.documentUpdateNotes.success,
      payload: data,
    });
    if (cb && typeof cb.resolve === 'function') cb.resolve();
  } catch (err) {
    const error = err?.response?.data?.message;
    if (error) {
      yieldErrorNotification(error);
    } else {
      yieldErrorNotification(<FormattedMessage id="app.generalUnknownError" defaultMessage="Something went wrong" />);
    }
    yield doLogout(actions.documentUpdateNotes, err);
    if (cb && typeof cb.reject === 'function') cb.reject();
  }
}

export function* ensureUploadDocument({ payload, ...config }) {
  const formData = new global.FormData();

  formData.append('file', payload.file);

  if (payload.tags) {
    payload.tags.forEach((tag) => {
      formData.append('tags', tag);
    });
  }

  const params = {
    ...payload.params.toJS(),
  };
  if (payload.isConfidential) {
    Object.defineProperty(params, 'isConfidential', {
      enumerable: true,
      configurable: true,
      value: 1,
    });
  }
  try {
    const { data } = yield call(Api.uploadDocument, {
      data: formData,
      params,
      ...config,
    });
    yield put({
      type: actions.documentUploadAction.success,
      payload: data,
      id: payload.id,
    });
    if (typeof payload.cb === 'function') payload.cb();
    yield delay(5000);
    yield put(actions.queueRemoveAction([payload.id]));
  } catch (err) {
    if (axios.isCancel(err)) {
      yield put(actions.queueRemoveAction([payload.id]));
    } else if (err.response && (err.response.status || '').toString() === process.env.REACT_APP_UNAUTHORIZED_CODE) {
      yield put({
        type: signOutAction.type,
      });
    } else {
      yield put({
        type: actions.documentUploadAction.failure,
        err,
        payload,
      });
    }
  }
}

export function* ensureSplitDocument({ newdocs, documentID, splitOptions, resolve }) {
  try {
    yield call(Api.splitDocument, {
      data: {
        splitOptions,
        newdocs,
        documentID,
      },
    });

    const isGrid = yield select(isGridWorkSpaceSelector);
    yield put({
      type: actions.documentSplit.success,
      payload: {
        documentID,
        isGrid,
      },
    });
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    yield doLogout(actions.documentSplit, err);
  }
}

export function* ensureSignDocument({ signatureID, documentID }) {
  try {
    yield call(Api.signDocument, {
      data: {
        signatureID,
        documentID,
      },
    });
    const documents = yield select(selector.documentsByIdSelector);
    let doc = documents.get(documentID);
    if (!doc) {
      const linkedDocs = yield select(selector.documentLinkedSelector);
      doc = linkedDocs.get(documentID);
    }
    const newDocument = doc.update('tags', (tags) => tags.add(`_S_SIG_DOKKA${signatureID}_PENDING`));
    yield put({
      type: actions.documentSignDocument.success,
      payload: {
        ...documentAdapter(newDocument.toJS()),
        documentID,
      },
    });
  } catch (err) {
    const errorMessage = err?.response?.data?.message;

    if (errorMessage) {
      yieldWarningNotification(errorMessage);
    }
    yield doLogout(actions.documentSignDocument, err);
  }
}

export function* ensureSignShowDocument({ signatureID, documentID }) {
  try {
    yield call(Api.signDocument, {
      data: {
        signatureID,
        documentID,
      },
    });
    const document = yield select(selector.documentSelector);
    const newDocument = document.update('tags', (tags) => tags.add(`_S_SIG_DOKKA${signatureID}_PENDING`));
    yield put({
      type: actions.documentSignShowDocument.success,
      payload: {
        ...documentAdapter(newDocument.toJS()),
      },
    });
  } catch (err) {
    const errorMessage = err?.response?.data?.message;

    if (errorMessage) {
      yieldWarningNotification(errorMessage);
    }

    yield doLogout(actions.documentSignShowDocument, err);
  }
}

export function ensureExportData(params) {
  return function* exportDataGen({ payload }) {
    try {
      const category = getNestedCategory(params);
      const isConfidential = params.companyType === 'confidential';
      const { format, linkedId } = payload;
      // This ensures token is refreshed, if it is expired.
      // Get requests use cookies for auth without exposing token in url
      yield call(tokenKeeper.getToken);
      const companyId = yield call(tokenKeeper.getCompany);
      const language = yield call(tokenKeeper.getLanguage);
      const baseParams = { companyId, language, outputFormat: format, isConfidential };
      const queryParams = linkedId
        ? // for link panel we send linkId only
          {
            ...baseParams,
            linkPanelID: linkedId,
          }
        : {
            // for workspace we send category and filters (as query)
            // to export filtered documents only
            ...baseParams,
            ...(yield select(Router.querySelector)),
            category,
          };

      download(Api.exportData(queryParams));
    } catch (err) {
      yield doLogout(actions.nextDocumentAction, err);
    }
  };
}

function* actualizeLinkedDocuments(documents) {
  const linkedDocuments = yield select(selector.documentLinkedSelector);
  const [...linkedDocumentsIds] = linkedDocuments.keys();
  const updatedLinkedDocuments = documents.filter(({ documentID }) => linkedDocumentsIds.includes(documentID));

  const documentsForMerge = documentsAdapter({
    items: updatedLinkedDocuments,
  }).list;

  if (updatedLinkedDocuments.length) {
    yield put({
      type: actions.documentsBulkUpdateAction.success,
      payload: { linked: linkedDocuments.merge(documentsForMerge) },
    });
  }
}

export function* actualizeDocuments(documents) {
  const currentDocumentList = yield select(selector.documentsByIdSelector);
  const documentsWereChanged = documentsListAdapter(documents);
  const mergedDocumentList = mergeDocumentsMapWithPreviousGridMeta(currentDocumentList, documentsWereChanged);

  yield fork(ensureWorkspaceGridRefreshServerSide, false);

  yield put({
    type: actions.documentsBulkUpdateAction.success,
    payload: {
      documents: mergedDocumentList,
    },
  });
}

export function* ensureChangeDocumentsType(documentsIds, type, resolve, reject) {
  try {
    const requestData = {
      documentID: documentsIds,
      document_type: type,
    };

    const { data } = yield call(Api.changeTypeDocuments, { data: requestData });

    // Grid's doctype tabs / sub-type chips come from the documents/aggregations TanStack query
    // (da-19295 D9); reclassification changes those counts/facets. Only grid needs the refetch —
    // tile refreshes via the worker → Redux — so gate on the view rather than relying on TanStack's
    // disabled-query semantics.
    const isGrid = yield select(isGridWorkSpaceSelector);
    if (isGrid) {
      queryClient.invalidateQueries({ queryKey: documentsKeys.aggregations() });
    }

    executeFunc(resolve, data);

    return data;
  } catch (err) {
    const error = (err && err.response && err.response.data && err.response.data.error) || '';
    if (error) {
      yieldErrorNotification(error);
    }
    executeFunc(reject, err);
    throw err;
  }
}

export function* ensureBulkChangeDocumentsType({ documentsIds, type, resolve, reject }) {
  try {
    const data = yield call(ensureChangeDocumentsType, documentsIds, type, resolve, reject);
    const { documents, ...stats } = data;
    const isGrid = yield select(isGridWorkSpaceSelector);

    yield call(actualizeLinkedDocuments, documents);
    if (isGrid) {
      yield call(actualizeDocuments, documents);
    }

    executeFunc(resolve, stats);
  } catch (err) {
    yield doLogout(actions.documentsBulkUpdateAction, err);
  }
}

export function* ensureBulkApprove({ data, resolve, reject }) {
  try {
    const { data: response } = yield call(Api.bulkApprove, {
      data,
    });

    executeFunc(resolve, response);

    return response;
  } catch (err) {
    executeFunc(reject, err);
    yield doLogout(actions.documentsBulkApproveAction, err);
    throw err;
  }
}

export function* ensureBulkApproveOnWorkspace({ data, resolve, reject }) {
  try {
    const response = yield call(ensureBulkApprove, {
      data,
      resolve,
      reject,
    });
    const { documents } = response;
    const isGrid = yield select(isGridWorkSpaceSelector);

    yield call(actualizeLinkedDocuments, documents);

    if (isGrid) {
      yield call(actualizeDocuments, documents);
    }

    executeFunc(resolve, response);
  } catch (err) {
    executeFunc(reject, err);
    yield doLogout(actions.documentsBulkApproveAction, err);
  }
}

// write-type is now a service tag: _T_GEN, _T_FIN, or a subtype id (_S_IS_*).
// Financial intent = base _T_FIN OR any subtype (backend treats a subtype as financial).
const isFinancialWriteType = (type) => type === CONST.tags.financial || type.startsWith('_S_IS_');

export function* ensureSetTypeDocument(documentID, type, resolve, reject) {
  try {
    const { location } = yield select(routing);
    const { documents } = yield call(ensureChangeDocumentsType, [documentID], type, resolve, reject);
    const isDocumentRoute = matchRoute.isDocument(location.pathname);
    const isCompanyRoute = matchRoute.isCompanyWorkspace(location.pathname);

    // if we are on the document page, then we need to update the approvals data, so we use different adapter
    const adapter = isDocumentRoute ? documentAdapterValidated : documentAdapter;
    if (documents[0]) {
      yield put({
        type: actions.documentUpdate.success,
        payload: adapter(documents[0]),
      });
    }

    if (isDocumentRoute) {
      if (isFinancialWriteType(type)) {
        yield all([call(Journal.ensureTextMap, documentID), call(Journal.ensureJounalEntry, documentID)]);
      } else {
        yield call(Journal.ensureTextMap, documentID);
      }
    }

    if (isCompanyRoute) {
      yield fork(ensureWorkspaceGridRefreshServerSide, false);
    }

    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject(err);
    yield doLogout(actions.documentsChangeTypesAction, err);
  }
}

export const ensureSetAsFinancialDocument = (documentID, subtype, resolve, reject) =>
  ensureSetTypeDocument(documentID, subtype ?? CONST.tags.financial, resolve, reject);

export const ensureSetAsGeneralDocument = (documentID, resolve, reject) =>
  ensureSetTypeDocument(documentID, CONST.tags.general, resolve, reject);

export function* ensureSignDocuments(documentsIds, signatureId, resolve, reject) {
  const requestData = {
    signatureID: signatureId,
    documentID: documentsIds,
  };

  try {
    const { data } = yield call(Api.signDocuments, { data: requestData });
    const { documents, ...stats } = data;

    yield call(actualizeLinkedDocuments, documents);

    executeFunc(resolve, stats);
  } catch (err) {
    executeFunc(reject, err);
    yield doLogout(actions.documentsBulkUpdateAction, err);
  }
}

export function* ensureBulkAccept({ documentsIds, resolve, reject }) {
  try {
    const { data } = yield call(Api.bulkAcceptDocuments, {
      data: {
        documentID: documentsIds,
      },
    });
    const { documents } = data;
    const isGrid = yield select(isGridWorkSpaceSelector);

    yield call(actualizeLinkedDocuments, documents);

    if (isGrid) {
      yield call(actualizeDocuments, documents);
    }

    executeFunc(resolve, data);
  } catch (err) {
    executeFunc(reject, err);
    yield doLogout(actions.documentsBulkUpdateAction, err);
  }
}

export function* ensureMergeDocuments({ payload: { documentID, resolve = (x) => x, reject = (x) => x } }) {
  const requestData = { documentID };

  const isGrid = yield select(isGridWorkSpaceSelector);
  try {
    const { data } = yield call(Api.mergeDocuments, { data: requestData });
    yield put({
      type: actions.mergeDocumentsAction.success,
      payload: {
        isGrid,
        deletedDocumentIds: documentID,
      },
    });

    resolve(data);

    yieldSuccessNotification(
      <FormattedMessage
        values={{
          success: data.success.length,
          total: documentID.size,
        }}
        id="document.actions.merge"
        defaultMessage="{success} out of {total} documents were merged"
      />,
    );
  } catch (err) {
    reject(err);
    yield doLogout(actions.mergeDocumentsAction, err);
  }
}

export function* ensureMergeDocumentsForPrinting({ payload: { documentID, resolve = (x) => x, reject = (x) => x } }) {
  const requestData = { documentID };

  try {
    const response = yield call(Api.mergeDocumentsForPrinting, {
      data: requestData,
      responseType: 'arraybuffer',
      headers: {
        Accept: 'application/pdf',
      },
    });
    const pdfFile = new window.Blob([response.data], {
      type: 'application/pdf',
    });
    resolve(pdfFile);
  } catch (err) {
    const error = get(['response', 'data'])(err);
    if (error) {
      const msg = isArrayBuffer(error) ? decodeArrayBufferText(error) : error;
      yieldErrorNotification(
        typeof msg === 'string' ? (
          msg
        ) : (
          <FormattedMessage
            id="selectedPanel.toast.print.fetch.error"
            defaultMessage="We encountered a printing issue, please try again"
          />
        ),
      );
    }
    notify.captureEvent(err);
    reject(err);
    yield doLogout(actions.mergeDocumentsForPrintingAction, err);
  }
}

export function* ensureDownloadDocuments({ payload: { documentID, linkid } }) {
  try {
    const {
      data: { downloadUrl },
    } = yield call(Api.getDocumentsDownloadURL, { data: { documentID, linkID: linkid } });
    window.open(downloadUrl, '_blank');
  } catch (err) {
    yield doLogout(actions.documentsDownloadDocumentsAction, err);
  }
}

export function* ensureRemoveDocuments({ payload: { documentID, resolve, reject } }) {
  try {
    const { data } = yield call(Api.removeDocuments, {
      data: { documentID },
    });
    if (data.success?.length > 0) {
      yield fork(ensureWorkspaceGridApplyTransaction, {
        transactionType: 'remove',
        nodes: data.success,
      });
    }
    yield put({
      type: actions.documentsRemoveDocumentsAction.success,
      payload: { movedIDs: data.success, nonLinkedDocumentIDs: data.success },
    });
    resolve(data);
  } catch (err) {
    reject(err);
    yield doLogout(actions.documentsRemoveDocumentsAction, err);
  }
}

export function* ensureReprocessDocuments({ payload: { documentIds, resolve, reject } }) {
  try {
    const { data } = yield call(Api.reprocessDocuments, {
      data: { document_ids: documentIds },
    });
    resolve(data);
  } catch (err) {
    reject(err);
    yield doLogout(actions.reprocessDocumentsAction, err);
  }
}

export function* ensureStopDocumentProcessing(documentID) {
  yield put({ type: actions.documentStopProcessingAction.request });
  try {
    const { data } = yield call(Api.stopDocumentProcessing, {
      data: { documentID },
    });
    yield put({
      type: actions.documentStopProcessingAction.success,
      payload: documentAdapter(data),
    });
  } catch (err) {
    yield doLogout(actions.documentStopProcessingAction, err);
  }
}

export function* getViewArrangementStoreKey() {
  const userGUID = yield select(userGUIDSelector);
  const jeInvoiceType = yield select(jeInvoiceTypeSelector);
  return userGUID ? [userGUID, jeInvoiceType ?? 'regular'].join('-') : null;
}

export function* ensureGetViewArrangement() {
  const storeKey = yield call(getViewArrangementStoreKey);
  if (storeKey) {
    try {
      const data = yield indexedDb.arrangementOptions.get(storeKey);
      if (data) {
        yield put({
          type: actions.documentGetViewArrangementAction.success,
          payload: data.view,
        });
      }
    } catch (err) {
      yield doLogout(actions.documentGetViewArrangementAction, err);
    }
  }
}

export function* ensureUpdateViewArrangement({ payload }) {
  const storeKey = yield call(getViewArrangementStoreKey);
  if (storeKey) {
    try {
      yield indexedDb.arrangementOptions.put({
        jeType: storeKey,
        view: payload,
      });
      yield put({
        type: actions.documentUpdateViewArrangementAction.success,
        payload,
      });
    } catch (err) {
      yield doLogout(actions.documentUpdateViewArrangementAction, err);
    }
  }
}

export function* ensureResetDocumentsList() {
  yield put({
    type: actions.documentClearAll.type,
    payload: null,
  });
}

export function* ensureGetEmailPayload({ payload: { documentID, resolve, reject } }) {
  try {
    const { data } = yield call(Api.getEmailData, {
      params: { documentID },
    });
    yield put({
      type: actions.getDocumentEmailPayloadAction.success,
      payload: data,
    });

    executeFunc(resolve, data);
  } catch (err) {
    executeFunc(reject, err);
    yield doLogout(actions.getDocumentEmailPayloadAction, err);
  }
}

export function* ensureGetHotkeyList() {
  try {
    yield put({
      type: actions.getDocumentHotkeysAction.request,
    });

    const { data } = yield call(Api.getDocumentHotkeys);
    yield put({
      type: actions.getDocumentHotkeysAction.success,
      payload: adapters.documentHotkeysAdapter(data),
      companyId: yield select(selector.currentCompanyIdSelector),
    });
  } catch (err) {
    yield doLogout(actions.getDocumentHotkeysAction, err);
  }
}

function* _ensureSaveGridPreset(data) {
  const apiCall = data.id ? Api.updatePreset : Api.createPreset;
  const {
    data: { result, error },
  } = yield call(apiCall, { data, params: { type: 'grid', version: 'be_driven_grid' } });
  if (result) {
    yield put({
      type: actions.documentsGridSavePresetAction.success,
      payload: result,
    });
    yield put({
      type: actions.documentsGridClearConfigurationChangesAction.type,
      payload: null,
    });
  } else if (error) {
    yieldErrorNotification(error);
  }
}

const extractNewPreset = (prev, current) => {
  const prevIds = prev.map((i) => i.id);
  return find(current, (i) => !prevIds.includes(i.id));
};

export function* ensureSetPreset(presetId) {
  yield put({
    type: actions.documentsGridSetCurrentPresetIdAction.type,
    payload: presetId,
  });

  yield put({
    type: actions.documentsGridClearConfigurationChangesAction.type,
    payload: null,
  });
}

export function* ensureSetNewPreset(prevPresets) {
  // if new preset exists set it as current preset
  const currentPresets = yield select(selector.gridRawPresetsSelector);
  const newPreset = extractNewPreset(prevPresets, currentPresets);
  if (newPreset) {
    yield call(ensureSetPreset, newPreset.id);
  }
}

export function* ensureSaveGridPreset({ payload: { name, isAll, id = null } }) {
  try {
    const prevPresets = yield select(selector.gridRawPresetsSelector);
    const userId = yield select(userGUIDSelector);
    const erpType = yield select(currentConnectedERPselector);
    const {
      gridColumnsState: gridColumns,
      gridFilters,
      gridSorting,
    } = yield select(selector.gridCurrentSettingsStateSelector);

    const saveData = {
      id,
      name,
      user_id: userId,
      erp_type: erpType,
      ...(isAll ? {} : { company_id: yield select(selector.currentCompanyIdSelector) }),
      config: { gridFilters, gridSorting, gridColumns },
    };
    debugger; // eslint-disable-line no-debugger
    yield call(_ensureSaveGridPreset, saveData);
    yield call(ensureSetNewPreset, prevPresets);
  } catch (err) {
    yield doLogout(actions.documentsGridSavePresetAction, err);
  }
}

export function* ensureDeleteGridPreset({ payload }) {
  const currentPresetId = yield select(selector.gridCurrentPresetIdSelector);
  try {
    const {
      data: { result, error },
    } = yield call(Api.deletePreset, {
      data: {
        id: payload,
      },
      params: { type: 'grid' },
    });
    if (result) {
      yield put({
        type: actions.documentsGridDeletePresetAction.success,
        payload: result,
      });
    } else if (error) {
      yieldErrorNotification(error);
    }

    // if delete current preset
    if (currentPresetId === payload) {
      yield call(ensureSetPreset, null);
    }
  } catch (err) {
    yield doLogout(actions.documentsGridDeletePresetAction, err);
  }
}

export function* ensureGetGridPresets() {
  try {
    const {
      data: { result, error },
    } = yield call(Api.getPresets, {
      params: { type: 'grid', version: 'be_driven_grid' },
    });

    if (result) {
      yield put({
        type: actions.documentsGridGetPresetsAction.success,
        payload: result,
      });
    } else if (error) {
      yieldErrorNotification(error);
    }
  } catch (err) {
    yield doLogout(actions.documentsGridGetPresetsAction, err);
  }
}

export function* ensureGetSharesPreset({ payload }) {
  try {
    const {
      data: { result, error },
    } = yield call(Api.getPreset, {
      id: payload,
      params: { type: 'grid' },
    });
    if (result) {
      yield put({
        type: actions.documentsGridGetSharedPresetAction.success,
        payload: result,
      });
    } else if (error) {
      yieldErrorNotification(error);
    }
  } catch (err) {
    yield doLogout(actions.documentsGridGetSharedPresetAction, err);
  }
}

export function* ensureSaveCurrentPresetId({ payload }) {
  const userId = yield select(userIdSelector);
  const companyId = yield select(selector.currentCompanyIdSelector);
  if (userId && companyId) {
    const data = yield indexedDb.gridCurrentPresetIds.get(userId);
    const companies = (data && data.companies) || {};
    indexedDb.gridCurrentPresetIds.put({ userId, companies: { ...companies, [companyId]: payload } });
  }
}

export function* ensureLoadCurrentPresetId() {
  const userId = yield select(userIdSelector);
  const companyId = yield select(selector.currentCompanyIdSelector);

  if (userId && companyId) {
    const data = yield indexedDb.gridCurrentPresetIds.get(userId);
    if (data && data.companies[companyId]) {
      yield put({
        type: actions.documentsGridRestoreResentPresetIdAction.success,
        payload: data.companies[companyId],
      });
    }
  }
}

export function* ensureStoreGridFilterChanges({ payload: { filters } }) {
  const companyId = yield select(selector.currentCompanyIdSelector);
  const currentPresetId = yield select(selector.gridCurrentPresetIdSelector);
  updateGridConfigChanges({ gridFilters: filters, companyId, currentPresetId });
}

export function* ensureStoreGridSortingChanges({ payload: { sorting } }) {
  const companyId = yield select(selector.currentCompanyIdSelector);
  const currentPresetId = yield select(selector.gridCurrentPresetIdSelector);
  updateGridConfigChanges({ gridSorting: sorting, companyId, currentPresetId });
}

export function* ensureStoreGridColumnsChanges({ payload }) {
  const companyId = yield select(selector.currentCompanyIdSelector);
  const currentPresetId = yield select(selector.gridCurrentPresetIdSelector);
  updateGridConfigChanges({ gridColumns: payload, companyId, currentPresetId });
}

export function ensureDeleteGridPresetChanges() {
  deleteGridConfigChanges();
}

export function* ensureRestoreGridPresetChanges() {
  const companyId = yield select(selector.currentCompanyIdSelector);
  const currentPresetId = yield select(selector.gridCurrentPresetIdSelector);

  const currentStored = getCurrentStoredConfig();

  if (currentStored.companyId === companyId && currentStored.currentPresetId === currentPresetId) {
    yield put({
      type: actions.documentsGridColumnsAppliedAction.type,
      payload: currentStored.gridColumns,
    });

    yield put({
      type: actions.documentsGridFilterAppliedAction.type,
      payload: { filters: currentStored.gridFilters },
    });

    yield put({
      type: actions.documentsGridSortingAppliedAction.type,
      payload: { sorting: currentStored.gridSorting },
    });
  }

  yield put({
    type: actions.documentsGridPresetLoadedAction.type,
    payload: true,
  });
}

export function* ensureIgnoreDocumentWarnings({ payload }) {
  const { resolve, reject, documentId } = payload;
  try {
    yield put({
      type: actions.documentIgnoreWarningsAction.request,
    });
    yield call(Api.ignoreDocumentWarnings, {
      data: { documentID: documentId, ignoreWarning: true },
    });
    yield put({
      type: actions.documentIgnoreWarningsAction.success,
      payload: documentId,
    });
    yield fork(ensureWorkspaceGridRefreshServerSide, false);
    yieldInfoNotification(
      <FormattedMessage id="document.actions.ignoreWarnings.success" defaultMessage="Warnings ignored" />,
    );
    if (typeof resolve === 'function') resolve();
  } catch (err) {
    if (typeof reject === 'function') reject(err);
    yield doLogout(actions.documentIgnoreWarningsAction, err);
  }
}

export function* ensureWorkerHeaderTablesCorrected({ documentID, MW, withoutCheck = false }) {
  // eslint-disable-next-line require-yield
  function* prepare() {
    return { documentID };
  }

  function* checkDisabled() {
    if (withoutCheck) return false;
    const shouldProcessingDocBeBlocked = yield select(selector.shouldProcessingDocBeBlockedSelector);
    return !shouldProcessingDocBeBlocked;
  }

  return {
    apiName: 'getDocument',
    config: {
      params: yield call(prepare),
    },
    prepare,
    disabled: yield call(checkDisabled),
    success(data) {
      if (data?.tags.length && !data.tags.includes(CONST.tags.isBeingProcessed)) {
        if (MW) {
          MW.terminate('getDocument');
        }
        return {
          type: actions.headerTablesCorrectionAction.success,
          payload: { data, documentID },
        };
      }
      return { type: actions.documentUpdate.success, payload: documentAdapter(data) };
    },
    failure(err) {
      return logoutAction(actions.documentGet, err);
    },
  };
}

export function* ensureHeaderTablesCorrection({ documentID, headerWords, MW }) {
  try {
    yield call(Api.headerTablesCorrection, {
      data: { table_header_hint: headerWords, documentID },
    });
    yield call(MW.init, ensureWorkerHeaderTablesCorrected, { documentID, MW, withoutCheck: true });
  } catch (err) {
    yield doLogout(actions.headerTablesCorrectionAction, err);
  }
}

export function* ensureUpdateDocumentAfterCorrectedTableHeader({ documentID, data }) {
  try {
    const res = yield call(ensureGetExtractedTableData, documentID);

    if (isEmpty(res.items_table)) {
      yieldErrorNotification(
        <FormattedMessage id="toast.notRecognizedTable" defaultMessage="The table was not recognized." />,
      );
    }

    yield put({ type: actions.documentUpdate.success, payload: documentAdapter(data) });
  } catch (err) {
    yield doLogout(actions.headerTablesCorrectionAction, err);
  }
}

export function* ensureSendDocumentByEmail({ payload }) {
  const { to, cc, subject, description, linkID, documentIds, emailsForLS, resolve, reject } = payload;
  try {
    const data = yield call(Api.sendDocByEmail, {
      data: { email_to: to, cc, subject, message: description, linkID, document_ids: documentIds },
    });

    if (emailsForLS.length) {
      storage().emailSuggestion().set(JSON.stringify(emailsForLS));
    }

    const recipients = [...to, ...(cc || [])].join(', ');

    yieldSuccessNotification(
      <FormattedMessage
        values={{
          emails: recipients,
        }}
        id="form.dialog.sendByEmail.toast.success"
        defaultMessage={`Document(s) sent to ${recipients} successfully`}
      />,
    );
    resolve(data);
  } catch (err) {
    yieldErrorNotification(err.response.data);
    reject(err);
    yield doLogout(actions.sendDocumentByEmailAction, err);
  }
}

export function* ensureDuplicateAsMultiGrvInvoice({ payload }) {
  const { resolve, reject, documentID } = payload;
  try {
    const { data } = yield call(Api.duplicateAsMultiGrv, {
      data: { document_id: documentID },
    });

    const { href, label, message } = data;

    yieldSuccessNotification(
      <>
        {message}
        <br />
        <br />
        <Link
          href={href}
          underline="always"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontSize: '14px', color: 'common.white', fontWeight: '400', textDecorationColor: 'inherit', mt: 1 }}
        >
          {label}
        </Link>
      </>,
    );
    resolve(data);
  } catch (err) {
    handleErrorResponseWithToast(err);
    reject(err);
    yield doLogout(actions.sendDocumentByEmailAction, err);
  }
}

export function* permanentlyDeleteDocuments({ documentID, resolve, reject }) {
  try {
    const { data } = yield call(Api.purgeDocuments, {
      data: { document_ids: documentID },
    });
    if (data.success?.length > 0) {
      yield fork(ensureWorkspaceGridApplyTransaction, {
        transactionType: 'remove',
        nodes: data.success,
      });
    }
    yield put({
      type: actions.permanentlyDeleteDocumentsAction.success,
      payload: { movedIDs: data.success, nonLinkedDocumentIDs: data.success },
    });
    resolve(data);
  } catch (err) {
    reject(err);
    yield doLogout(actions.permanentlyDeleteDocumentsAction, err);
  }
}

export function* permanentlyDeleteAllDocuments() {
  try {
    yield call(Api.purgeAllDocuments);

    yield put({
      type: actions.documentClearAll.type,
      payload: null,
    });

    yield fork(ensureWorkspaceGridRefreshServerSide, true);

    yield put({
      type: actions.permanentlyDeleteAllDocumentsAction.success,
    });
    yieldInfoNotification(
      <FormattedMessage id="toast.permanentlyDeleteAll" defaultMessage="All documents permanently deleted" />,
    );
  } catch (err) {
    yield doLogout(actions.permanentlyDeleteAllDocumentsAction, err);
  }
}

export function* ensureInvalidateDocumentCache() {
  // add documentID or linkId to success action payload so that we can check if doc is linked and run cache invalidation
  yield put({
    type: actions.documentCacheInvalidationAction.type,
    payload: true,
  });
}

export function* ensureGetDocumentPayments({ payload: { documentID }, resolve, reject }) {
  try {
    const { data } = yield call(Api.getDocumentPayments, {
      params: { documentID },
    });

    yield put({ type: actions.getDocumentPaymentsAction.success });

    resolve?.(data);
  } catch (err) {
    reject?.(err);
    yield doLogout(actions.getDocumentPaymentsAction, err);
  }
}

export function* ensureBulkCreateDocumentPayment({ payload: { documentsIds, resolve, reject } }) {
  try {
    const { data } = yield call(Api.bulkCreateDocumentPayment, { data: { documentID: documentsIds } });
    resolve?.(data);
    yield put({ type: actions.bulkCreateDocumentPaymentAction.success });
  } catch (err) {
    yieldErrorNotification(err?.response?.data);
    reject?.(err);
    yield doLogout(actions.bulkCreateDocumentPaymentAction, err);
  }
}

export function* ensureCreateDocumentPayment({ documentID, resolve, reject }) {
  try {
    const data = yield call(Api.createDocumentPayment, {
      params: { documentID },
    });

    yieldSuccessNotification(
      <FormattedMessage
        id="document.toast.preparePayment.success"
        defaultMessage="Payment preparation completed successfully"
      />,
    );

    resolve?.([data]);
    yield put({ type: actions.createDocumentPaymentAction.success });
  } catch (err) {
    yieldErrorNotification(err.response.data);
    reject?.(err);
    yield doLogout(actions.createDocumentPaymentAction, err);
  }
}
